import { useMemo, useState } from 'react';
import { Spin, Tabs, TabsProps } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import PatientInformationHeader from '../../components/Patient/PatientInformationHeader';
import {
    useGetPatientDetailsQuery,
    useGetPatientMedicationsQuery,
} from '../../features/patient/patientService';
import RoundedButton from '../../components/RoundedButton';
import ConfirmationModal from '../../components/Orders/ConfirmationModal';
import PageTitle from '../../components/PageTitle';
import RenderSideItem from '../../components/RenderSideItem';
import MedOrdersCard from '../../components/MedOrders/MedOrdersCard';
import moment from 'moment';
import { MedOrderResponse } from '../../models/PatientModels';
import MedSheetsTab from '../../components/Orders/MedSheets';
import { Utility } from '../../utilities/Utility';

const MedHistory = () => {
    const navigate = useNavigate();
    const { id: patientId = '' } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

    const { data: patientDetails, isLoading: patientDetailsLoading } =
        useGetPatientDetailsQuery({
            id: patientId,
        });

    const { data: patientMedications, isLoading } =
        useGetPatientMedicationsQuery({
            patientId: patientId,
        });

    const activeMedications = useMemo(() => {
        if (patientMedications && patientMedications?.length > 0) {
            const activeMeds = patientMedications.filter((pm) => {
                return moment(pm.endDate).isSameOrAfter(moment());
            });
            return activeMeds;
        }
        return [];
    }, [patientMedications]);

    const discontinuedMedications = useMemo(() => {
        if (patientMedications && patientMedications?.length > 0) {
            const activeMeds = patientMedications.filter((pm) => {
                return moment(pm.endDate).isBefore(moment());
            });
            return activeMeds;
        }
        return [];
    }, [patientMedications]);

    const isDisabled =
        isLoading || (patientMedications && patientMedications.length === 0);

    const items: TabsProps['items'] = useMemo(() => {
        return [
            {
                key: '1',
                label: 'Med Sheets',
                children: <MedSheetsTab setIsErrorModalOpen={setIsErrorModalOpen} />,
            },
            {
                key: '2',
                label: 'Fulfilled Medications',
                children: (
                    <MedOrdersCard
                        data={activeMedications as MedOrderResponse[]}
                        isLoading={isLoading || patientDetailsLoading}
                        organizationImplementationDate={Utility.getFormattedDate(patientDetails?.hospiceOrganizationImplementationDate)}
                    />
                ),
            },
            {
                key: '3',
                label: 'Discontinued Medications',
                children: (
                    <MedOrdersCard
                        data={discontinuedMedications as MedOrderResponse[]}
                        isLoading={isLoading || patientDetailsLoading}
                        organizationImplementationDate={Utility.getFormattedDate(patientDetails?.hospiceOrganizationImplementationDate)}
                    />
                ),
            },
        ];
    }, [activeMedications, discontinuedMedications, isLoading]);

    return (
        <Spin spinning={patientDetailsLoading || isLoading}>
            <div className="">
                <PageTitle
                    title="Medication History"
                    onClick={() => navigate(`/patient/${patientId}`)}
                />
                <div className="mx-5">
                    {patientDetails && (
                        <PatientInformationHeader
                            patient={patientDetails}
                            navigate={() => navigate(-1)}
                        >
                            <RenderSideItem
                                link={`/patient/${patientId}/allergies`}
                                title="Drug Allergies"
                                iconSource="/assets/rx-images/patient-dashboard/patient-allergies.png"
                            />
                        </PatientInformationHeader>
                    )}
                    <Tabs
                        className="mt-4 order-card-tabs"
                        defaultActiveKey="1"
                        items={items}
                        tabBarExtraContent={{
                            right: (
                                <div className="d-flex gap-3">
                                    <RoundedButton
                                        onClick={() =>
                                            navigate(
                                                `/patient/${patientId}/prescriptions`,
                                            )
                                        }
                                        style={{
                                            ...(!isDisabled
                                                ? {
                                                      backgroundColor:
                                                          'var(--hcrx-medium-blue)',
                                                      color: 'white',
                                                  }
                                                : {}),
                                            fontWeight: 500,
                                        }}
                                        disabled={isDisabled}
                                    >
                                        <div className="d-flex gap-2">
                                            Access Re-prescribe
                                        </div>
                                    </RoundedButton>
                                </div>
                            ),
                        }}
                    />
                    <ConfirmationModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title='Successful!'
                        message='The medication(s) were successfully sent'
                        primaryButtonText='Review Medication'
                        secondaryButtonText='View Medication Queue'
                        onPrimaryButtonLinkTo={`/patient/${patientId}/approve-medications/view`}
                        onSecondaryButtonLinkTo={`/patient/${patientId}/add-new-medication`}
                    />
                    <ConfirmationModal
                        isOpen={isErrorModalOpen}
                        onClose={() => setIsErrorModalOpen(false)}
                        isSuccess={false}
                        title='Failed'
                        message='Medication was not found in Scriptsure. Review and correct the Med Sheet details in the EMR or create the Med Order via NaviScript'
                        primaryButtonText='Create Med Order via NaviScript'
                        secondaryButtonText='View Med Sheets'
                        onPrimaryButtonLinkToClick={()=> {
                            setIsErrorModalOpen(false);
                            navigate(`/patient/${patientId}/med-order-create`);
                        }}
                        onSecondaryButtonLinkClick={()=>setIsErrorModalOpen(false)}
                    />
                </div>
            </div>
        </Spin>
    );
};

export default MedHistory;
